import React, { useEffect, useRef } from "react";
import { Input } from "./../../Components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./../../Components/ui/select";
import { removeUnderscores } from "./../../Utils/helperFunction";

const formatOptions = (option) =>
  option
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const DynamicForm = ({
  formData,
  errors,
  handleInputChange,
  handlefeildBlur,
  formattedFields,
}) => {
  // Create refs for each field
  const fieldRefs = useRef({});
  const hasScrolledToError = useRef(false);

  // Focus on the first error when errors change
  useEffect(() => {
    const errorKeys = Object.keys(errors);

    // Only scroll to errors if we haven't already and there are errors
    if (errorKeys.length > 0 && !hasScrolledToError.current) {
      // Find the first error that has a non-empty error message
      for (const key of errorKeys) {
        if (errors[key] && fieldRefs.current[key]) {
          // Scroll to the element
          fieldRefs.current[key].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Try to focus on the input if possible
          if (fieldRefs.current[key].querySelector("input, select")) {
            fieldRefs.current[key].querySelector("input, select").focus();
          }

          // Mark that we've scrolled to an error
          hasScrolledToError.current = true;
          break;
        }
      }
    }
  }, [errors]);

  // Reset the scroll flag when errors are cleared
  useEffect(() => {
    if (Object.keys(errors).length === 0) {
      hasScrolledToError.current = false;
    }
  }, [errors]);

  // Render input based on field type
  const renderInput = (field) => {
    const isError = errors[field.fieldName];
    const baseClassName = "text-sm border font-geomanistBold w-full";
    const errorClassName = isError ? "border-red-500" : "";

    switch (field.fieldName) {
      case "type":
        return (
          <Select
            value={formData[field.fieldName]}
            onValueChange={(value) => handleInputChange(field.fieldName, value)}
          >
            <SelectTrigger className={`${baseClassName} ${errorClassName}`}>
              <SelectValue placeholder={`Select ${field.displayName}`} />
            </SelectTrigger>
            <SelectContent className="z-[999]">
              {field.validation?.split("|").map((option) => (
                <SelectItem key={option} value={option}>
                  {formatOptions(option)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case "entity_type":
      case "bank_country_code":
      case "account_currency":
      case "payment_method":
      case "local_clearing_system":
      case "country_code":
        return (
          <Select
            value={formData[field.fieldName]}
            onValueChange={(value) => handleInputChange(field.fieldName, value)}
          >
            <SelectTrigger className={`${baseClassName} ${errorClassName}`}>
              <SelectValue placeholder={`Select ${field.displayName}`} />
            </SelectTrigger>
            <SelectContent className="z-[999]">
              {field.validation?.split("|").map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      default:
        if (
          field?.fieldName !== "account_routing_type1" &&
          field?.fieldName !== "account_routing_type2"
        ) {
          return (
            <Input
              type="text"
              placeholder={field.displayName}
              value={formData[field.fieldName]}
              onChange={(e) =>
                handleInputChange(field.fieldName, e.target.value)
              }
              onBlur={(e) => handlefeildBlur(field.fieldName, e.target.value)}
              className={`${baseClassName} ${errorClassName} text-sm border font-geomanistBold`}
            />
          );
        }
        return null; // Return null if no match is found.
    }
  };

  return (
    <div className="space-y-4">
      {formattedFields?.map((field) => (
        <div
          key={field.fieldName}
          className="space-y-2"
          ref={(el) => (fieldRefs.current[field.fieldName] = el)}
        >
          {field?.fieldName !== "account_routing_type1" &&
          field?.fieldName !== "account_routing_type2" ? (
            <label className="block text-xs text-blackShade font-geomanistBold mb-2">
              {field.displayName === "Swift Code"
                ? "SWIFT Code"
                : field.displayName}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
          ) : null}
          {renderInput(field)}
          {errors[field.fieldName] && (
            <p className="text-xs text-red-500 mt-1">
              {removeUnderscores(errors[field.fieldName])}
            </p>
          )}
        </div>
      ))}
    </div>
  );
};

export default DynamicForm;
